import {useTranslation} from '@aboutyou/i18n';
import {styled} from '@linaria/react';
import React, {
  FC,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useId,
  useState,
} from 'react';
import {COLORS} from '../../../constants/colors';
import {SPACING} from '../../../constants/sizing';
import {UnstyledButton} from '../../atoms/Buttons/UnstyledButton';
import {
  ContentPopper,
  ContentPopperProps,
} from '../../atoms/ContentPopper/ContentPopper';
import {
  PrimaryContentPopperButton,
  SecondaryContentPopperButton,
} from '../../atoms/ContentPopper/ContentPopperButtons/ContentPopperButtons';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {tabFocusedStyles} from '../../atoms/TabFocused/tabFocusedStyles';
import {
  TermsAndConditionsBody,
  TermsAndConditionsHeader,
} from '../../atoms/TermsAndConditions/TermsAndConditions';
import {CountdownBoxed} from '../Countdown/CountdownBoxed';
import {bannerWideAndUpLinaria} from './bannerContainer';

/**
 * Content of the sheet that opens from the info icon — either plain display
 * text or a link CTA, never both (mirrors the banner schema's InfoIcon
 * variant oneof).
 */
export type BannerInfoIconContent =
  | {
      type: 'displayText';
      headline: string;
      /**
       * Body content rendered inside the info sheet. Use ReactNode so callers
       * compose trusted markup directly — never pass raw, unsanitized HTML.
       */
      body: ReactNode;
    }
  | {
      type: 'cta';
      /** Button label configured in CRM. */
      label: string;
      /** Opens the CRM-configured link target; the sheet closes first. */
      onClick: VoidFunction;
    };

export interface BannerInfoIconData {
  content: BannerInfoIconContent;
  /**
   * End of the promotion, shown as a countdown inside the sheet. Comes from
   * the info icon's own schema field only — the sheet must not fall back to
   * the on-banner countdown (ASFE-14355).
   */
  countdownEndsAt?: Date;
}

/**
 * Countdown rendered above the info-sheet header. The two colors come from the
 * banner's `ColorSet` (a designed contrast pair): `backgroundColor` fills the
 * bar, `foregroundColor` tints the countdown text + clock icon.
 */
interface BannerInfoIconCountdown {
  endsAt: Date;
  /** Solid fill behind the countdown bar. */
  backgroundColor?: string;
  /** Tint for the countdown text + clock icon. */
  foregroundColor?: string;
}

interface BannerInfoIconProps {
  content: BannerInfoIconContent;
  /** Tint applied to the info icon. Defaults to black. */
  foregroundColor?: string;
  /**
   * Optional countdown shown above the sheet header. Only the mobile `Flyout`
   * renders `aboveHeaderElement`; the desktop `ModalDialog` ignores it, so the
   * countdown is mobile-only by construction (matching the legacy
   * `InfoCircleWithDialog`).
   */
  countdown?: BannerInfoIconCountdown;
  /**
   * Layout used for the info sheet — typically `Flyout` for mobile (bottom
   * sheet) or `ModalDialog` for desktop. The component wires up the
   * surrounding ContentPopper, header, body and close button.
   */
  ContentPopperLayoutComponent: ContentPopperProps['LayoutComponent'];
  /**
   * When true, drops the absolute top-right positioning so the icon can be
   * placed inside a parent flex group (e.g. next to the "sponsored" label
   * on SponsoredBrandBanner).
   */
  inline?: boolean;
  className?: string;
  ['data-testid']?: string;
}

export const BannerInfoIcon: FC<BannerInfoIconProps> = ({
  content,
  foregroundColor,
  countdown,
  ContentPopperLayoutComponent,
  inline = false,
  className,
  'data-testid': dataTestid = 'promotionBanner-infoIcon',
}) => {
  const {t} = useTranslation();
  const [open, setOpen] = useState(false);
  const sheetId = useId();

  const handleOpen = useCallback((e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleCtaClick = useCallback(() => {
    setOpen(false);
    if (content.type === 'cta') {
      content.onClick();
    }
  }, [content]);

  const tint = foregroundColor ?? COLORS.Black;
  const isDisplayText = content.type === 'displayText';

  return (
    <>
      <IconButton
        aria-label={t('Open info window')}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={sheetId}
        className={className}
        data-testid={dataTestid}
        data-banner-info-icon=""
        data-inline={inline ? '' : undefined}
        onClick={handleOpen}
      >
        <GlassDisk />
        <StyledIcon
          asset={GenericIconName.Info}
          width="20px"
          height="20px"
          fill={tint}
        />
      </IconButton>
      <div id={sheetId}>
        <ContentPopper
          LayoutComponent={ContentPopperLayoutComponent}
          open={open}
          onClose={handleClose}
          renderInPortal
          aboveHeaderElement={
            countdown && (
              <SheetCountdownBar
                data-testid="promotionBanner-sheetCountdown"
                style={{backgroundColor: countdown.backgroundColor}}
              >
                <CountdownBoxed
                  endDate={countdown.endsAt}
                  size="small"
                  // Bar fill lives on the wrapper; keep the inner box
                  // transparent so we don't stack a second background.
                  backgroundColor="transparent"
                  fontColor={countdown.foregroundColor}
                />
              </SheetCountdownBar>
            )
          }
          headerContent={
            isDisplayText ? (
              <TermsAndConditionsHeader>
                {content.headline}
              </TermsAndConditionsHeader>
            ) : undefined
          }
          bodyContent={
            isDisplayText ? (
              <TermsAndConditionsBody>{content.body}</TermsAndConditionsBody>
            ) : undefined
          }
          footerContent={
            isDisplayText ? (
              <SecondaryContentPopperButton
                label={t('Close')}
                onClick={handleClose}
              />
            ) : (
              <StackedFooterButtons>
                <PrimaryContentPopperButton
                  label={content.label}
                  onClick={handleCtaClick}
                  data-testid="promotionBanner-sheetCta"
                />
                <SecondaryContentPopperButton
                  label={t('Close')}
                  onClick={handleClose}
                />
              </StackedFooterButtons>
            )
          }
        />
      </div>
    </>
  );
};

const IconButton = styled(UnstyledButton)`
  position: absolute;
  top: 8px;
  inset-inline-end: 8px;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &[data-inline] {
    position: relative;
    top: auto;
    inset-inline-end: auto;
  }

  &:focus-visible {
    ${tabFocusedStyles({offset: '-2px'})}
  }

  ${bannerWideAndUpLinaria(`
    top: 24px;
    inset-inline-end: 24px;

    &[data-inline] {
      top: auto;
      inset-inline-end: auto;
    }
  `)}
`;

const GlassDisk = styled.span`
  position: absolute;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.24);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
`;

const StyledIcon = styled(Icon)`
  position: relative;
`;

/**
 * Full-width countdown bar tucked above the sheet header. Mirrors the legacy
 * `InfoCircleWithDialog` treatment: rounded top corners to hug the Flyout's
 * top, and a small negative bottom margin so it overlaps the header edge.
 */
const SheetCountdownBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 0;
  padding: 6px 0 12px;
  border-radius: 16px 16px 0 0;
  margin-bottom: -6px;
`;

/**
 * The layouts' FooterWrapper lays two children out side by side; the link-CTA
 * sheet needs the CRM button stacked full-width above Close, so both live in
 * one column that the wrapper treats as a single full-width child.
 */
const StackedFooterButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING['Y.Md']};
  width: 100%;

  & > * {
    width: 100%;
  }
`;
